/**
 * CapacityManagement — page (CES standard)
 * The module's main section, split into two tabs:
 *   - Per-asset management (granular): inventory of racks/droplets with
 *     slotting edits one asset at a time.
 *   - Outpost-wide changes (bulk): the whole-Outpost capacity editor.
 * Both tabs lead with the Outpost selector; nothing is selected by default,
 * the user picks an Outpost to load it.
 */

import { useNavigate, useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { EditCapacity } from './EditCapacity';
import { Inventory } from './Inventory';
import { useOutposts } from '../hooks/useOutposts';

export type CapacityTab = 'inventory' | 'edit';

const TAB_CLASSES = 'cursor-pointer border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors';

function tabPath(tab: CapacityTab, outpostId?: string): string {
  if (tab === 'inventory') return outpostId ? `/outposts/${outpostId}` : '/outposts';
  return outpostId ? `/outposts/${outpostId}/edit` : '/outposts/edit';
}

export function CapacityManagement({ tab }: { tab: CapacityTab }) {
  const { outpostId } = useParams<{ outpostId: string }>();
  const navigate = useNavigate();
  const { outposts, loading, error } = useOutposts();

  const tabButton = (target: CapacityTab, label: string, testId: string) => (
    <button
      type="button"
      role="tab"
      aria-selected={tab === target}
      data-testid={testId}
      className={`${TAB_CLASSES} ${
        tab === target
          ? 'border-orange-500 text-orange-600'
          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
      }`}
      onClick={() => navigate(tabPath(target, outpostId))}
    >
      {label}
    </button>
  );

  return (
    <>
      <h2 className="mb-1 text-2xl font-bold text-gray-900">Capacity management</h2>
      <p className="mb-4 text-sm text-gray-500">
        {tab === 'inventory'
          ? 'Granular administration: inspect racks and droplets, and edit slotting one asset at a time'
          : 'Bulk administration: reconfigure capacity across ALL active assets of an Outpost at once'}
      </p>

      <div
        className="mb-5 flex gap-1 border-b border-gray-200"
        role="tablist"
        aria-label="Capacity management mode"
      >
        {tabButton('inventory', 'Per-asset management (granular)', 'tab-per-asset')}
        {tabButton('edit', 'Outpost-wide changes (bulk)', 'edit-outpost')}
      </div>

      {loading ? (
        <p className="py-8 text-center text-gray-500">Loading outposts…</p>
      ) : error ? (
        <p className="py-8 text-center text-red-600">{error}</p>
      ) : outposts.length === 0 ? (
        <div className="py-10 text-center" data-testid="no-outposts-registered">
          <p className="mb-2 font-medium text-gray-700">No Outposts registered</p>
          <p className="text-sm text-gray-500">
            This module only manages the Outposts you register.{' '}
            <Link to="/outposts/settings" className="text-orange-600 underline hover:text-orange-700">
              Register one in the Outpost registry
            </Link>{' '}
            to get started.
          </p>
        </div>
      ) : (
        <>
          <div className="mb-8 flex flex-wrap gap-3" role="tablist" aria-label="Outposts">
            {outposts.map((outpost) =>
              outpost.reachable === false ? (
                <div
                  key={outpost.outpostId}
                  className="min-w-[240px] rounded-lg border border-red-300 bg-red-50 px-5 py-4 text-left"
                  data-testid={`outpost-unreachable-${outpost.outpostId}`}
                >
                  <p className="mb-1 font-bold text-gray-900">
                    {outpost.name || outpost.outpostId}
                  </p>
                  <span className="flex gap-2 text-xs text-gray-500">
                    <span>{outpost.outpostId}</span>
                    <span>·</span>
                    <span className="font-semibold text-red-600">UNREACHABLE</span>
                  </span>
                  {outpost.registrationError && (
                    <p className="mt-1 max-w-[280px] truncate text-xs text-red-600">
                      {outpost.registrationError}
                    </p>
                  )}
                </div>
              ) : (
                <button
                  key={outpost.outpostId}
                  type="button"
                  role="tab"
                  aria-selected={outpost.outpostId === outpostId}
                  className={`min-w-[240px] cursor-pointer rounded-lg border bg-white px-5 py-4 text-left shadow transition-all hover:shadow-md ${
                    outpost.outpostId === outpostId
                      ? 'border-orange-500 ring-1 ring-orange-500'
                      : 'border-gray-200'
                  }`}
                  onClick={() => navigate(tabPath(tab, outpost.outpostId))}
                >
                  <p className="mb-1 font-bold text-gray-900">{outpost.name}</p>
                  <span className="flex gap-2 text-xs text-gray-500">
                    <span>{outpost.outpostId}</span>
                    <span>·</span>
                    <span>{outpost.availabilityZone}</span>
                    <span>·</span>
                    <span>{outpost.lifeCycleStatus}</span>
                  </span>
                </button>
              ),
            )}
          </div>

          {!outpostId ? (
            <p className="py-8 text-center text-sm text-gray-500" data-testid="select-outpost-hint">
              Select an Outpost above to{' '}
              {tab === 'inventory'
                ? 'browse its racks and droplets'
                : 'plan an Outpost-wide capacity change'}
            </p>
          ) : tab === 'inventory' ? (
            <Inventory />
          ) : (
            <EditCapacity embedded />
          )}
        </>
      )}
    </>
  );
}
